import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'i18n';

const NS = 'menuManagementEditDrawer';

const PriceInputField = lazy(
  () => import('components/MenuManagement/MenuEdit/Fields/PriceInputField'),
);
const ParentChildSelectorField = lazy(
  () => import('components/MenuManagement/MenuEdit/Fields/ParentChildSelectorFields'),
);
const PhotoThumbnailField = lazy(() =>
  import('components/MenuManagement/MenuEdit/Fields/PhotoThumbnailField').then(m => ({
    default: m.PhotoThumbnailField,
  })),
);
const NumberInputField = lazy(() =>
  import('components/MenuManagement/MenuEdit/Fields/NumberInputField').then(m => ({
    default: m.NumberInputField,
  })),
);
const ClassificationsSelectorField = lazy(
  () => import('components/MenuManagement/MenuEdit/Fields/ClassificationsSelectorField'),
);
const StringField = lazy(() => import('components/MenuManagement/MenuEdit/Fields/StringField'));
const PosId = lazy(() => import('components/MenuManagement/Fields/PosId'));
const AliasName = lazy(() => import('components/MenuManagement/Fields/AliasName'));
const DropdownSelectorField = lazy(
  () => import('components/MenuManagement/MenuEdit/Fields/DropdownSelectorField'),
);
const CustomTaxField = lazy(
  () => import('components/MenuManagement/MenuEdit/Fields/CustomTaxField'),
);
const MultipleModifiersCheckboxField = lazy(() =>
  import('components/MenuManagement/MenuEdit/Fields/MultipleModifiersCheckboxField').then(m => ({
    default: m.MultipleModifiersCheckboxField,
  })),
);

const FIELD_COMPONENTS = {
  price: PriceInputField,
  parentSelector: ParentChildSelectorField,
  childSelector: ParentChildSelectorField,
  number: NumberInputField,
  classifications: ClassificationsSelectorField,
  itemId: StringField,
  posId: PosId,
  aliasName: AliasName,
  dropdownSelector: DropdownSelectorField,
  customTaxComboBox: CustomTaxField,
  allowMultipleModifiersCheckbox: MultipleModifiersCheckboxField,
  default: StringField,
  posLock: StringField,
};

function EditDrawerFieldRenderer({ column, form, record, ...rest }) {
  const { inputType, dataIndex, title, text } = column;
  const { t } = useTranslation(NS);

  const FieldComponent =
    FIELD_COMPONENTS[inputType] || FIELD_COMPONENTS[dataIndex] || FIELD_COMPONENTS.default;

  let fieldContent = null;

  switch (inputType) {
    case 'parentSelector':
    case 'childSelector':
      fieldContent = (
        <FieldComponent
          form={form}
          title={title}
          dataIndex={dataIndex}
          record={record}
          selectedOptions={
            inputType === 'parentSelector' ? rest.selectedParents : rest.selectedChildren
          }
          setSelectedOptions={
            inputType === 'parentSelector' ? rest.setSelectedParents : rest.setSelectedChildren
          }
          noEditParentChild={rest.noEditParentChild}
          setTaxRateEnabled={rest.setTaxRateEnabled}
          excludedModGroups={rest.excludedModGroups}
          excludedModifiers={rest.excludedModifiers}
          nestedModGroupEnabled={rest.nestedModGroupEnabled}
        />
      );
      break;
    case 'upload':
      fieldContent = (
        <PhotoThumbnailField
          form={form}
          record={record}
          onRemove={rest.handleRemoveImage}
          setUploadImageFile={rest.setUploadImageFile}
          isUploading={rest.isUploading}
        />
      );
      break;
    case 'classifications':
      fieldContent = (
        <FieldComponent
          form={form}
          title={title}
          dataIndex={dataIndex}
          record={record}
          selectedOptions={form.values.classifications}
          setSelectedOptions={rest.handleClassificationsChange}
        />
      );
      break;
    case 'dropdownSelector':
      fieldContent = (
        <FieldComponent
          form={form}
          record={record}
          text={text}
          dataIndex={dataIndex}
          options={[
            { value: 'true', label: t('EditDrawerFieldRenderer.setAsDefault') },
            { value: 'false', label: t('EditDrawerFieldRenderer.notDefault') },
          ]}
          onChange={value => {
            form.setFieldValue('isDefault', value);
          }}
        />
      );
      break;
    case 'customTaxComboBox':
      if (rest.showIntegrationFields || rest.customTaxRateEnabled) {
        fieldContent = (
          <FieldComponent
            form={form}
            record={record}
            title={title}
            taxRate={form.values.taxRate}
            setTaxRate={value => form.setFieldValue('taxRate', value)}
            noEditParentChild={rest.noEditParentChild}
            setNoEditParentChild={rest.setNoEditParentChild}
            updateTaxRate={rest.updateTaxRate}
            setUpdateTaxRate={rest.setUpdateTaxRate}
            taxRateEnabled={rest.taxRateEnabled}
          />
        );
      }
      break;
    case 'aliasName':
      if (rest.showIntegrationFields) {
        fieldContent = (
          <FieldComponent
            form={form}
            record={record}
            title={title}
            dataIndex={dataIndex}
            disableAliasName={rest.disableAliasName}
            setDisablePosId={rest.setDisablePosId}
          />
        );
      }
      break;
    case 'posId':
      fieldContent = (
        <FieldComponent
          form={form}
          record={record}
          title={title}
          dataIndex={dataIndex}
          posLock={rest.posLock}
          setPosLock={rest.setPosLock}
          disablePosId={rest.disablePosId}
          setDisableAliasName={rest.setDisableAliasName}
        />
      );
      break;
    default:
      fieldContent = (
        <FieldComponent form={form} dataIndex={dataIndex} record={record} title={title} />
      );
  }

  return <Suspense fallback={null}>{fieldContent}</Suspense>;
}

EditDrawerFieldRenderer.propTypes = {
  column: PropTypes.shape({
    inputType: PropTypes.string,
    dataIndex: PropTypes.string,
    title: PropTypes.string,
    text: PropTypes.string,
  }).isRequired,
  form: PropTypes.shape(),
  record: PropTypes.shape(),
};

export default EditDrawerFieldRenderer;
